import { Injectable, Type } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Params } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map, mergeMap, tap } from 'rxjs/operators';

import { DashboardComponent } from '../../dashboard/dashboard.component';
import { ConfigService } from '../services/config.service';
import { GoToService } from '../../core/components/go-to-input/go-to.service';

declare const gtag;

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  private _currentUrlPath: string;
  private _currentUrlParams: Params;
  private _currentTitle: string;
  private _previousUrlPath: string;
  private _previousUrlParams: Params;
  private _previousTitle: string;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private titleService: Title,
              private configService: ConfigService,
              private goToService: GoToService) {
    this._currentUrlPath = null;
    this._currentTitle = 'Dashboard';
    this.navigationEndHandler();
  }

  get currentUrlPath() {
    return this._currentUrlPath;
  }

  get currentUrlParams() {
    return this._currentUrlParams;
  }

  get currentTitle() {
    return this._currentTitle;
  }

  get previousUrlPath() {
    return this._previousUrlPath || '/';
  }

  get previousUrlParams() {
    return this._previousUrlParams;
  }

  get previousTitle() {
    return this._previousTitle || 'Dashboard';
  }

  get title() {
    return this.titleService.getTitle();
  }

  set title(title: string) {
    this.titleService.setTitle(title);
  }

  isDashboardActive() {
    return (DashboardComponent === this.getDeepestRoute().component);
  }

  reset() {
    this._currentUrlPath = null;
    this._currentUrlParams = null;
    this._currentTitle = null;
    this._previousUrlPath = null;
    this._previousUrlParams = null;
    this._previousTitle = null;
  }

  getRouteByComponent(component: Type<any>): ActivatedRoute {
    let route = this.route;

    do {
      if (route.component) {
        if (component === route.component) {
          return route;
        }
      }
      route = route.firstChild;
    }
    while (route);

    return null;
  }

  private isString(value: any): value is string {
    return typeof value === 'string';
  }

  private getDeepestRoute(): ActivatedRoute {
    let route = this.route;
    while (route.firstChild) { // get deepest route
      route = route.firstChild;
    }
    return route;
  }

  private onNavigationEnd(data: any) {
    this._previousTitle = this.currentTitle;
    this._currentTitle = data.title;
    this.title = data.title;
    this.goToService.reset();
  }

  private onRouteChange(route: ActivatedRoute) {
    this._previousUrlPath = this.currentUrlPath;
    this._previousUrlParams = this.currentUrlParams;
    this._currentUrlPath = window.location.pathname; // path w/o query params
    this._currentUrlParams = route.snapshot.queryParams;
  }

  private navigationEndHandler() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(route => this.getDeepestRoute()),
      filter(route => route.outlet === 'primary'),
      tap(route => this.onRouteChange(route)),
      mergeMap(route => route.data)
    ).subscribe((data) => this.onNavigationEnd(data));
  }

}
